// Sprint 5 — Módulo de Clientes CRM

import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UserPlus, Search, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { Button } from "@/components/ui/button";
import { CreateClientModal } from "@/components/dashboard/create-client-modal";
import { ClientDetailDrawer, type ClientListRow } from "@/components/dashboard/client-detail-drawer";
import { WhatsAppChatLink, CopyPhoneButton } from "@/components/dashboard/whatsapp-chat-link";
import { ExportClientsButton } from "@/components/dashboard/export-clients-button";

type SortKey = "displayName" | "email" | "phone" | "appointmentCount";

function rowKey(r: ClientListRow): string {
  return r.clientId ?? `e:${r.email}`;
}

function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function avatarColor(name: string): string {
  const colors = [
    "linear-gradient(135deg, #6366f1, #818cf8)",
    "linear-gradient(135deg, #8b5cf6, #a78bfa)",
    "linear-gradient(135deg, #0ea5e9, #38bdf8)",
    "linear-gradient(135deg, #10b981, #34d399)",
    "linear-gradient(135deg, #f59e0b, #fbbf24)",
    "linear-gradient(135deg, #ef4444, #f87171)",
    "linear-gradient(135deg, #ec4899, #f472b6)",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active)
    return (
      <ChevronsUpDown
        size={12}
        strokeWidth={2}
        className={css({ opacity: 0.3, flexShrink: 0 })}
      />
    );
  return active && dir === "asc" ? (
    <ChevronUp size={12} strokeWidth={2.5} className={css({ flexShrink: 0 })} />
  ) : (
    <ChevronDown size={12} strokeWidth={2.5} className={css({ flexShrink: 0 })} />
  );
}

export function DashboardClients() {
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const effectiveProfessionalId = searchParams.get("professionalId");
  const professionalQuery = effectiveProfessionalId
    ? `?professionalId=${encodeURIComponent(effectiveProfessionalId)}`
    : "";

  const [rows, setRows] = useState<ClientListRow[] | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("displayName");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [search, setSearch] = useState("");
  const [drawerRow, setDrawerRow] = useState<ClientListRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLockEmail, setCreateLockEmail] = useState(false);
  const [createInitialEmail, setCreateInitialEmail] = useState<string | undefined>(undefined);

  const loadRows = useCallback(async () => {
    const r = await fetchWithSessionSync(
      `/api/dashboard/clients${professionalQuery}`,
      { credentials: "include" },
      refresh,
    );
    if (!r.ok) return;
    setRows((await r.json()) as ClientListRow[]);
  }, [refresh, professionalQuery]);

  const refreshListAndSyncDrawer = useCallback(async () => {
    const r = await fetchWithSessionSync(
      `/api/dashboard/clients${professionalQuery}`,
      { credentials: "include" },
      refresh,
    );
    if (!r.ok) return;
    const data = (await r.json()) as ClientListRow[];
    setRows(data);
    setDrawerRow((prev) => {
      if (!prev) return prev;
      return data.find((x) => x.email === prev.email) ?? prev;
    });
  }, [refresh, professionalQuery]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync(
        `/api/dashboard/clients${professionalQuery}`,
        { credentials: "include" },
        refresh,
      );
      if (cancelled) return;
      if (!r.ok) {
        setRows([]);
        return;
      }
      setRows((await r.json()) as ClientListRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh, professionalQuery]);

  const filteredAndSorted = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter(
          (r) =>
            r.displayName.toLowerCase().includes(q) ||
            (r.email ?? "").toLowerCase().includes(q) ||
            (r.phone ?? "").includes(q),
        )
      : rows;
    const mul = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "displayName":
          cmp = a.displayName.localeCompare(b.displayName, "es", { sensitivity: "base" });
          break;
        case "email":
          cmp = (a.email ?? "").localeCompare(b.email ?? "", "es", { sensitivity: "base" });
          break;
        case "phone":
          cmp = (a.phone ?? "").localeCompare(b.phone ?? "", "es", { sensitivity: "base" });
          break;
        case "appointmentCount":
          cmp = a.appointmentCount - b.appointmentCount;
          break;
        default:
          break;
      }
      return cmp * mul;
    });
  }, [rows, sortKey, sortDir, search]);

  function onHeaderClick(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function openCreateFromToolbar() {
    setCreateLockEmail(false);
    setCreateInitialEmail(undefined);
    setCreateOpen(true);
  }

  function openCreateFromDrawer(email: string) {
    setCreateLockEmail(true);
    setCreateInitialEmail(email);
    setCreateOpen(true);
    setDrawerOpen(false);
  }

  if (!rows) {
    return (
      <DashboardPageShell eyebrow="Contactos" title="Clientes">
        <LoadingFrame variant="light" minHeight={260} />
      </DashboardPageShell>
    );
  }

  const thBtn = css({
    display: "inline-flex",
    alignItems: "center",
    gap: "1.5",
    py: "3",
    px: "4",
    color: "#94a3b8",
    fontWeight: "600",
    textAlign: "left",
    w: "full",
    cursor: "pointer",
    border: "none",
    bg: "transparent",
    fontSize: "11px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    transition: "opacity 120ms ease",
    _hover: { opacity: 0.65 },
  });

  return (
    <DashboardPageShell
      eyebrow="Contactos"
      title="Clientes"
      description="Contactos con perfil CRM o detectados por reservas. Hacé click en cualquier fila para ver el detalle."
      actions={
        <div className={css({ display: "flex", alignItems: "center", gap: "2.5" })}>
          <ExportClientsButton professionalQuery={professionalQuery} />
          <Button
            variant="primary"
            type="button"
            onClick={openCreateFromToolbar}
            className={css({ display: "inline-flex", alignItems: "center", gap: "1.5" })}
          >
            <UserPlus size={15} strokeWidth={2.5} aria-hidden />
            Crear cliente
          </Button>
        </div>
      }
    >
      {rows.length === 0 ? (
        <DashboardEmpty
          title="Sin clientes todavía"
          hint="Creá un perfil con el botón de arriba o recibí reservas para ver contactos automáticos."
        />
      ) : (
        <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          {/* Buscador */}
          <div className={css({ position: "relative", maxW: "340px" })}>
            <Search
              size={14}
              strokeWidth={2}
              className={css({
                position: "absolute",
                left: "3",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                pointerEvents: "none",
              })}
            />
            <input
              type="search"
              placeholder="Buscar por nombre, email o teléfono…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={css({
                w: "full",
                pl: "9",
                pr: "4",
                py: "2.5",
                fontSize: "sm",
                bg: "white",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                rounded: "lg",
                color: "#0f172a",
                _placeholder: { color: "#94a3b8" },
                _focus: {
                  outline: "none",
                  borderColor: "#6366f1",
                  boxShadow: "0 0 0 3px rgba(99,102,241,0.1)",
                },
              })}
            />
          </div>

          <div className={cx(cf.dashSurface, css({ overflow: "hidden" }))}>
            <div className={css({ overflowX: "auto" })}>
              {filteredAndSorted.length === 0 ? (
                <div
                  className={css({
                    py: "14",
                    textAlign: "center",
                    color: "#94a3b8",
                    fontSize: "sm",
                  })}
                >
                  Sin resultados para &ldquo;{search}&rdquo;
                </div>
              ) : (
                <table
                  className={css({
                    w: "full",
                    minW: "640px",
                    borderCollapse: "collapse",
                    fontSize: "sm",
                  })}
                >
                  <thead>
                    <tr
                      className={css({
                        bg: "#f8fafc",
                        borderBottomWidth: "1px",
                        borderColor: "#e2e8f0",
                      })}
                    >
                      <th className={css({ py: "0", px: "0", w: "40%" })}>
                        <button
                          type="button"
                          className={thBtn}
                          onClick={() => onHeaderClick("displayName")}
                        >
                          Nombre{" "}
                          <SortIcon active={sortKey === "displayName"} dir={sortDir} />
                        </button>
                      </th>
                      <th className={css({ py: "0", px: "0" })}>
                        <button
                          type="button"
                          className={thBtn}
                          onClick={() => onHeaderClick("email")}
                        >
                          Email <SortIcon active={sortKey === "email"} dir={sortDir} />
                        </button>
                      </th>
                      <th className={css({ py: "0", px: "0" })}>
                        <button
                          type="button"
                          className={thBtn}
                          onClick={() => onHeaderClick("phone")}
                        >
                          Teléfono <SortIcon active={sortKey === "phone"} dir={sortDir} />
                        </button>
                      </th>
                      <th className={css({ py: "0", px: "0" })}>
                        <button
                          type="button"
                          className={thBtn}
                          onClick={() => onHeaderClick("appointmentCount")}
                        >
                          Reservas{" "}
                          <SortIcon active={sortKey === "appointmentCount"} dir={sortDir} />
                        </button>
                      </th>
                      <th
                        className={css({
                          py: "3",
                          px: "4",
                          color: "#94a3b8",
                          fontWeight: "600",
                          fontSize: "11px",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          textAlign: "left",
                        })}
                      >
                        Contacto
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSorted.map((r) => (
                      <tr
                        key={rowKey(r)}
                        className={css({
                          borderTopWidth: "1px",
                          borderColor: "#f1f5f9",
                          cursor: "pointer",
                          _hover: { bg: "#f8fafc" },
                        })}
                        onClick={() => {
                          setDrawerRow(r);
                          setDrawerOpen(true);
                        }}
                      >
                        {/* Nombre con avatar */}
                        <td className={css({ py: "3", px: "4" })}>
                          <div
                            className={css({
                              display: "flex",
                              alignItems: "center",
                              gap: "3",
                            })}
                          >
                            <div
                              className={css({
                                w: "8",
                                h: "8",
                                rounded: "full",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                fontSize: "11px",
                                fontWeight: "700",
                                color: "white",
                                letterSpacing: "0.03em",
                                userSelect: "none",
                              })}
                              style={{ background: avatarColor(r.displayName) }}
                            >
                              {initials(r.displayName)}
                            </div>
                            <div className={css({ minW: 0 })}>
                              <p
                                className={css({
                                  fontWeight: "500",
                                  color: "#0f172a",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                })}
                              >
                                {r.displayName}
                              </p>
                              {!r.clientId && (
                                <p
                                  className={css({
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    letterSpacing: "0.05em",
                                    textTransform: "uppercase",
                                    color: "#94a3b8",
                                    mt: "0.5",
                                  })}
                                >
                                  Sin perfil
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        <td
                          className={css({
                            py: "3",
                            px: "4",
                            color: "#475569",
                            maxW: "0",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          })}
                        >
                          {r.email ?? ""}
                        </td>

                        <td className={css({ py: "3", px: "4", color: "#334155", whiteSpace: "nowrap" })}>
                          {r.phone ?? (
                            <span className={css({ color: "#cbd5e1" })}>—</span>
                          )}
                        </td>

                        <td className={css({ py: "3", px: "4" })}>
                          <span
                            className={css({
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minW: "6",
                              h: "5",
                              px: "2",
                              rounded: "full",
                              fontSize: "11px",
                              fontWeight: "600",
                              bg: r.appointmentCount > 0 ? "#ede9fe" : "#f1f5f9",
                              color: r.appointmentCount > 0 ? "#6d28d9" : "#94a3b8",
                            })}
                          >
                            {r.appointmentCount}
                          </span>
                        </td>

                        <td className={css({ py: "3", px: "4", verticalAlign: "middle" })}>
                          <div
                            className={css({
                              display: "flex",
                              gap: "1.5",
                              alignItems: "center",
                            })}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <WhatsAppChatLink phone={r.phone} variant="icon" stopRowClick />
                            <CopyPhoneButton phone={r.phone} stopRowClick />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <p
            className={css({
              fontSize: "xs",
              color: "#94a3b8",
              textAlign: "right",
            })}
          >
            {filteredAndSorted.length}{" "}
            {filteredAndSorted.length === 1 ? "contacto" : "contactos"}
            {search ? " encontrados" : " en total"}
          </p>
        </div>
      )}

      <ClientDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        row={drawerRow}
        effectiveProfessionalId={effectiveProfessionalId}
        onRefreshList={refreshListAndSyncDrawer}
        onOpenCreateProfile={openCreateFromDrawer}
        onDeleted={() => {
          setDrawerOpen(false);
          void loadRows();
        }}
      />

      <CreateClientModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void loadRows()}
        professionalQuery={professionalQuery}
        lockEmail={createLockEmail}
        initialEmail={createInitialEmail}
      />
    </DashboardPageShell>
  );
}
